import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  Stethoscope, PenNib, PaperPlaneTilt, ChartBar, Lock, Sparkle, Tag,
  ShieldCheck, ChatCircleText, FileText, UsersThree, Quotes, Check, X,
  Plus, CaretDown, ArrowRight,
  Heartbeat, Hand, Flask, ForkKnife, GenderFemale, Lightning, Virus,
  Eye, Ear, Baby, Wind, Brain, Bone, Drop,
  Prohibit, FirstAid, PersonSimpleWalk, Smiley, Moon,
  Buildings, Envelope, Clock, Info, TrendUp, Calculator,
} from "@phosphor-icons/react";

const HERO_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_clinical-forms-5/artifacts/s4t00zd9_Page-accueil-2.jpg";

export default function Landing() {
  const [yearly, setYearly] = useState(true);
  const [openFaq, setOpenFaq] = useState(-1);
  const [activeStep, setActiveStep] = useState(0);
  const stdPrice = yearly ? "26,10" : "29";
  const stdSuffix = yearly ? "/ mois TTC, soit 313,20€ / an, engagement annuel" : "/ mois TTC, sans engagement";
  const premPrice = yearly ? "44,10" : "49";
  const premSuffix = yearly ? "/ mois TTC, soit 529,20€ / an, engagement annuel" : "/ mois TTC, sans engagement";

  // Établissement — seat-based interactive pricing
  const SEATS_MIN = 3;
  const SEATS_MAX = 15;                        // ≥ 15 → offre sur mesure
  const SEAT_PRICE_MONTHLY = 39;               // € HT/siège/mois — mensuel
  const SEAT_PRICE_YEARLY = 35.10;             // € HT/siège/mois — annuel (−10%)
  const [seats, setSeats] = useState(5);
  const isCustomQuote = seats >= SEATS_MAX;
  const seatUnit = yearly ? SEAT_PRICE_YEARLY : SEAT_PRICE_MONTHLY;
  const seatTotal = useMemo(() => seats * seatUnit, [seats, seatUnit]);
  const seatYearlySavings = useMemo(() => (SEAT_PRICE_MONTHLY - SEAT_PRICE_YEARLY) * seats * 12, [seats]);
  const fmt = (n, fd = 2) => n.toLocaleString("fr-FR", { minimumFractionDigits: fd, maximumFractionDigits: fd });
  const mailtoQuote = `mailto:contact@questiomed.fr?subject=${encodeURIComponent(`Demande de devis — Établissement ${seats}+ sièges`)}&body=${encodeURIComponent(
    `Bonjour,\n\nJe souhaite obtenir un devis personnalisé pour Questiomed Établissement de santé.\n\n• Nombre de sièges praticien envisagé : ${seats}${isCustomQuote ? "+ (offre sur mesure)" : ""}\n• Facturation : ${yearly ? "annuelle" : "mensuelle"}\n• Fonctionnalités souhaitées : \n\nMerci de me recontacter.\n\nCordialement,`
  )}`;

  // ROI — Simulateur détaillé (source : coût horaire chargé secrétariat médical ~44€/h en France)
  const ROI_HOURS_PER_WEEK = 4;
  const ROI_SECRETARY_HOURLY = 44;
  const ROI_WEEKS_PER_MONTH = 4;
  const roiPerPractitionerMonthly = ROI_HOURS_PER_WEEK * ROI_SECRETARY_HOURLY * ROI_WEEKS_PER_MONTH; // 704€
  const roiSeatsForCalc = isCustomQuote ? SEATS_MAX : seats;
  const roiMonthlyTotal = roiPerPractitionerMonthly * roiSeatsForCalc;
  const roiYearlyTotal = roiMonthlyTotal * 12;
  const subscriptionMonthly = seatUnit * roiSeatsForCalc;
  const subscriptionYearly = subscriptionMonthly * 12;
  const roiNetYearly = roiYearlyTotal - subscriptionYearly;
  const roiMultiple = subscriptionYearly > 0 ? roiYearlyTotal / subscriptionYearly : 0;

  return (
    <div className="relative min-h-screen bg-[#F4F7FB] text-[#1E3A5F] overflow-hidden">
      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-home">
            <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
            <span className="text-[18px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="link-underline text-[#5F758F]">Fonctionnalités</a>
            <a href="#for-who" className="link-underline text-[#5F758F]">Pour qui ?</a>
            <a href="#security" className="link-underline text-[#5F758F]">Sécurité</a>
            <a href="#pricing" className="link-underline text-[#5F758F]">Tarifs</a>
            <a href="#faq" className="link-underline text-[#5F758F]">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login">
              <Button variant="ghost" className="rounded-full text-[#1E3A5F] hover:bg-[#E1E9F0]">Se connecter</Button>
            </Link>
            <Link to="/signup" data-testid="nav-signup">
              <Button className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white">Créer un compte</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-36 pb-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <h1 className="font-serif text-5xl md:text-[56px] leading-[1.1] tracking-tight mb-8">
              Vos questionnaires<br />
              patients <em className="italic font-bold text-[#3A8FA5]">en ligne</em>.<br />
              La consultation simplifiée.
            </h1>
            <p className="text-base md:text-lg text-[#5F758F] max-w-2xl leading-relaxed mb-10">
              Créez vos questionnaires facilement avec l'IA et notre bibliothèque validée.<br />
              Envoyez-les en un clic. Scoring automatique, données sécurisées.<br />
              Gagnez du temps.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/signup" data-testid="hero-cta-signup">
                <Button size="lg" className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white px-8 py-6 text-base group">
                  Commencer gratuitement
                  <ArrowRight size={18} weight="bold" className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="rounded-full border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#E1E9F0] hover:text-[#1E3A5F] hover:border-[#1E3A5F] px-8 py-6 text-base" data-testid="hero-cta-demo">
                Réserver une démo
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-[#5F758F]">
              <span className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[#3A8FA5]" /> 15 jours d'essai gratuit</span>
              <span className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[#3A8FA5]" /> Sans carte bancaire</span>
              <span className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[#3A8FA5]" /> Résiliable à tout moment</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="rounded-3xl overflow-hidden bg-[#E1E9F0] shadow-2xl shadow-[#1E3A5F]/10">
              <img src={HERO_IMG} alt="Patiente remplissant un questionnaire sur son téléphone en salle d'attente" className="w-full h-auto block" />
            </div>
            {/* PHQ-9 card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="absolute -bottom-6 -left-6 bg-white border border-[#E1E9F0] rounded-2xl p-5 max-w-xs shadow-xl"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-[#3A8FA5] mb-2">Score ISI</p>
              <div className="flex items-baseline gap-2">
                <p className="font-serif text-3xl">16</p>
                <p className="text-sm text-[#5F758F]">/ 28</p>
              </div>
              <p className="text-sm text-[#5F758F] mt-1">Insomnie clinique modérée</p>
              <div className="mt-3 h-1.5 rounded-full bg-[#E1E9F0] overflow-hidden">
                <div className="h-full w-[57%] bg-[#3A8FA5] rounded-full" />
              </div>
            </motion.div>
            {/* Small trust card top-right */}
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto rounded-3xl bg-white border border-[#E1E9F0] px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { n: "140+", l: "Questionnaires validés", i: FileText },
            { n: "1 clic", l: "Pour envoyer un questionnaire", i: PaperPlaneTilt },
            { n: "100%", l: "Données hébergées en France", i: ShieldCheck },
            { n: "< 24h", l: "Réponse du support", i: ChatCircleText },
          ].map(({ n, l, i: I }) => (
            <div key={l} className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#EEF3F8] flex items-center justify-center shrink-0">
                <I size={22} weight="regular" className="text-[#3A8FA5]" />
              </div>
              <div>
                <p className="font-serif text-2xl leading-none text-[#1E3A5F]">{n}</p>
                <p className="text-xs text-[#5F758F] mt-1.5">{l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section id="features" className="scroll-mt-24 px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Fonctionnalités</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
              Deux fonctionnalités pour vous faire <em className="italic text-[#3A8FA5]">gagner du temps</em>.
            </h2>
          </div>

          {/* ─── STANDARD ─── */}
          <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4 border-b border-[#E1E9F0] pb-5">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#3A8FA5]">Offre Standard</p>
              <h3 className="font-serif text-2xl md:text-3xl mt-1">Digitalisez l'envoi de vos questionnaires patients.</h3>
            </div>
            <a href="#pricing" className="text-xs tracking-[0.15em] uppercase text-[#3A8FA5] hover:underline underline-offset-4">Voir les tarifs →</a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-32">
            <div>
              <p className="text-sm italic text-[#5F758F] mb-8">Trois étapes. Zéro friction.</p>
              <ol className="space-y-3" data-testid="standard-steps">
                {[
                  { n: "01", t: "Créez votre base de questionnaires", d: "Rédigez vos questions, utilisez notre IA générative ou dupliquez un questionnaire validé parmi les plus de 140 disponibles dans notre bibliothèque." },
                  { n: "02", t: "Envoyez à vos patients", d: "En un clic, envoyez depuis l'application les questionnaires à vos patients — sans compte ni mot de passe à créer de leur côté." },
                  { n: "03", t: "Consultez les réponses", d: "Visibles en un clic, scores calculés, export PDF disponible pour vos dossiers patients." },
                ].map((s, i) => {
                  const active = activeStep === i;
                  return (
                    <li key={s.n}>
                      <button
                        type="button"
                        onClick={() => setActiveStep(i)}
                        onMouseEnter={() => setActiveStep(i)}
                        className={`w-full flex gap-6 items-start text-left rounded-2xl p-4 md:p-5 border transition-all ${active ? "bg-white border-[#3A8FA5]/40 shadow-sm" : "border-transparent hover:bg-white/60"}`}
                        data-testid={`step-${s.n}`}
                      >
                        <span className={`font-serif text-4xl leading-none shrink-0 transition-colors ${active ? "text-[#3A8FA5]" : "text-[#1E3A5F]/25"}`}>{s.n}</span>
                        <div>
                          <h4 className={`font-serif text-xl mb-2 transition-colors ${active ? "text-[#1E3A5F]" : "text-[#1E3A5F]/70"}`}>{s.t}</h4>
                          <p className={`leading-relaxed text-sm transition-colors ${active ? "text-[#5F758F]" : "text-[#5F758F]/70"}`}>{s.d}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
            <div className="relative min-h-[520px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35 }}
                  className="absolute inset-0"
                >
                  {activeStep === 0 && <BuilderMockup />}
                  {activeStep === 1 && <SendMockup />}
                  {activeStep === 2 && <ResultsMockup />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* ─── PREMIUM ─── */}
          <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4 border-b border-[#D45A3F]/30 pb-5">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#D45A3F]">Offre Premium</p>
              <h3 className="font-serif text-2xl md:text-3xl mt-1">Facilitez vos travaux de recherche clinique.</h3>
            </div>
            <a href="#pricing" className="text-xs tracking-[0.15em] uppercase text-[#3A8FA5] hover:underline underline-offset-4">Voir les tarifs →</a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1: Statistiques */}
            <div className="bg-white rounded-3xl p-10 border border-[#E1E9F0] min-h-[300px] flex flex-col justify-between relative overflow-hidden" data-testid="bento-stats">
              <div>
                <ChartBar size={32} weight="duotone" className="text-[#3A8FA5] mb-6" />
                <h3 className="font-serif text-3xl mb-4">Analyse statistique</h3>
                <p className="text-[#5F758F] leading-relaxed">
                  Regroupez vos patients par critères, comparez les scores dans le temps, croisez les cohortes
                  et exportez vos données prêtes pour R, Python, SPSS ou Excel.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-8">
                {["Sous-groupes", "Séries temporelles", "Comparaison inter-cohortes", "Export CSV"].map((t) => (
                  <span key={t} className="text-xs px-3 py-1 border border-[#E1E9F0] rounded-full text-[#5F758F]">{t}</span>
                ))}
              </div>
            </div>

            {/* Card 2: Analyse IA des CR */}
            <div className="bg-[#EEF3F8] rounded-3xl p-10 border border-[#3A8FA5]/25 min-h-[300px] flex flex-col justify-between relative overflow-hidden" data-testid="bento-ai">
              <div className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[#3A8FA5]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <Sparkle size={32} weight="duotone" className="text-[#3A8FA5] mb-6" />
                <h3 className="font-serif text-3xl mb-4">Analyse IA de vos comptes-rendus</h3>
                <p className="text-[#5F758F] leading-relaxed">
                  Importez vos comptes-rendus et laissez l'IA extraire automatiquement les variables cliniques recherchées
                  — antécédents, traitements, scores — pour alimenter votre étude sans re-saisie manuelle.
                </p>
              </div>
              <div className="relative flex flex-wrap gap-2 mt-8">
                {["Extraction structurée", "Variables cliniques"].map((t) => (
                  <span key={t} className="text-xs px-3 py-1 border border-[#3A8FA5]/30 rounded-full text-[#5F758F] bg-white/60">{t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Anonymization note */}
          <div className="mt-6 flex items-start md:items-center gap-3 rounded-2xl bg-[#EEF3F8] border border-[#E1E9F0] px-5 py-4" data-testid="anonymization-note">
            <Lock size={16} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5 md:mt-0" />
            <p className="text-sm text-[#5F758F] leading-relaxed">
              Toutes les données utilisées à des fins de recherche ou d'analyse sont <span className="text-[#1E3A5F] font-semibold">strictement anonymisées</span>. Aucune information nominative n'est jamais conservée dans les exports ni transmise à l'IA.
            </p>
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section id="for-who" className="scroll-mt-24 px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Pour qui ?</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1]">
              Un outil pensé pour <em className="italic text-[#3A8FA5]">tous</em> les professionnels de santé.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#5F758F] leading-relaxed">
              Questiomed aide les médecins, chirurgiens-dentistes, sages-femmes, masseurs-kinésithérapeutes, etc. dans leur pratique quotidienne et leur permet de gagner un temps précieux lors de leurs consultations.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: Prohibit, label: "Addictologie", ex: "AUDIT · Fagerström · CAGE" },
              { icon: Heartbeat, label: "Cardiologie", ex: "SF-36 · MLHFQ · KCCQ" },
              { icon: Hand, label: "Dermatologie", ex: "DLQI · POEM · PASI" },
              { icon: Flask, label: "Diabétologie", ex: "DTSQ · ADDQoL · SDSCA" },
              { icon: Lightning, label: "Douleur", ex: "EVA · BPI · DN4" },
              { icon: ForkKnife, label: "Gastro-entérologie", ex: "GSRS · IBS-QOL · IBDQ" },
              { icon: PersonSimpleWalk, label: "Gériatrie", ex: "MMSE · IADL · GDS" },
              { icon: Brain, label: "Neurologie", ex: "MoCA · MMSE · NIHSS" },
              { icon: Eye, label: "Ophtalmologie", ex: "NEI VFQ · VF-14" },
              { icon: Ear, label: "ORL", ex: "THI · VHI · SNOT-22" },
              { icon: Baby, label: "Pédiatrie", ex: "PedsQL · CBCL" },
              { icon: Wind, label: "Pneumologie", ex: "CAT · ACT · mMRC" },
              { icon: Bone, label: "Rhumatologie", ex: "HAQ · WOMAC · BASDAI" },
              { icon: Smiley, label: "Santé mentale", ex: "PHQ-9 · GAD-7 · BDI" },
              { icon: UsersThree, label: "Santé publique", ex: "EQ-5D · SF-12 · WHOQOL" },
              { icon: Moon, label: "Sommeil", ex: "PSQI · ESS · ISI" },
              { icon: Drop, label: "Urologie", ex: "IPSS · ICIQ · IIEF" },
            ].map(({ icon: I, label, ex }, idx) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
                className="group bg-white rounded-2xl p-5 border border-[#E1E9F0] hover:border-[#3A8FA5]/40 hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`specialty-${label.toLowerCase().split(" ")[0]}`}
              >
                <I size={22} weight="duotone" className="text-[#3A8FA5] mb-3 transition-transform group-hover:scale-110" />
                <p className="font-semibold text-sm text-[#1E3A5F] leading-tight">{label}</p>
                <p className="text-[11px] text-[#5F758F] mt-2 leading-snug">{ex}</p>
              </motion.div>
            ))}
            <div
              className="rounded-2xl p-5 border border-dashed border-[#3A8FA5]/40 bg-[#EEF3F8]/50 flex items-center justify-center"
              data-testid="specialty-more"
            >
              <p className="text-sm italic text-[#3A8FA5]">et bien d'autres…</p>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="px-6 py-32 bg-[#F4F7FB]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Avant / Après</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Gagnez du temps lors de vos consultations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* BEFORE */}
            <div className="bg-white rounded-3xl p-10 border border-[#E1E9F0] relative">
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5F758F] mb-5">
                <span className="w-6 h-px bg-[#5F758F]" /> Sans Questiomed
              </div>
              <h3 className="font-serif text-2xl text-[#5F758F] mb-6">Le questionnaire papier</h3>
              <ul className="space-y-3.5">
                {[
                  "Impression, distribution, ressaisie manuelle",
                  "Calcul des scores à la main, erreurs fréquentes",
                  "Perte de temps en consultation",
                  "Stockage physique — RGPD à risque",
                  "Suivi de l'évolution d'un patient difficile",
                  "Statistiques compliquées à réaliser",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[#5F758F]">
                    <span className="w-5 h-5 rounded-full bg-[#F4F7FB] border border-[#E1E9F0] flex items-center justify-center shrink-0 mt-0.5">
                      <X size={11} weight="bold" className="text-[#5F758F]" />
                    </span>
                    <span className="text-sm leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AFTER */}
            <div className="relative rounded-3xl p-10 border-2 border-[#3A8FA5] bg-gradient-to-br from-white to-[#EEF3F8] shadow-xl shadow-[#3A8FA5]/10">
              <span className="absolute -top-3 left-8 bg-[#3A8FA5] text-white text-xs tracking-[0.2em] uppercase px-3 py-1 rounded-full">Avec Questiomed</span>
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#3A8FA5] mb-5">
                <span className="w-6 h-px bg-[#3A8FA5]" /> Cabinet digitalisé
              </div>
              <h3 className="font-serif text-2xl mb-6">La consultation, augmentée</h3>
              <ul className="space-y-3.5">
                {[
                  "Envoi des questionnaires patients en un clic",
                  "Résultats lisibles facilement, scores calculés, export PDF facile",
                  "Gain de temps en consultation",
                  "Hébergement sur serveurs certifiés HDS en France et dans le respect du RGPD",
                  "Historique complet par patient, courbes d'évolution",
                  "Analyses statistiques faciles et extraction des données souhaitées de vos compte-rendus par IA",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[#1E3A5F]">
                    <span className="w-5 h-5 rounded-full bg-[#3A8FA5] flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={12} weight="bold" className="text-white" />
                    </span>
                    <span className="text-sm leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="px-6 pt-32 pb-32 bg-white border-t border-[#E1E9F0]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-3xl">
            <span id="security" className="block scroll-mt-24" aria-hidden="true" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Sécurité &amp; confidentialité</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1]">
              Les données de santé de vos patients, <em className="italic text-[#3A8FA5]">protégées</em> à chaque étape.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#5F758F] leading-relaxed">
              Nous savons à quel point les données de santé sont sensibles. C'est pourquoi nous avons conçu Questiomed pour protéger chaque information, à chaque étape, en respectant les exigences les plus strictes en matière de conformité et de sécurité du secteur médical en France et au sein de l'Union européenne.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 border-t border-[#E1E9F0]">
              {[
                {
                  n: "01",
                  t: "Hébergement sur serveurs HDS en France",
                  d: "Toutes les données de santé sont hébergées et traitées en France, sur une infrastructure certifiée Hébergeur de Données de Santé (HDS).",
                },
                {
                  n: "02",
                  t: "Conçu pour le RGPD et le secret médical",
                  d: "Questiomed respecte le RGPD et le Code de la santé publique. Les accès sont strictement contrôlés et chaque action est tracée et auditable.",
                },
                {
                  n: "03",
                  t: "Vous gardez la maîtrise",
                  d: "Les données médicales ne sont jamais réutilisées à d'autres fins.",
                },
              ].map((p) => (
                <div key={p.n} className="grid grid-cols-12 gap-6 py-8 border-b border-[#E1E9F0]" data-testid={`security-pillar-${p.n}`}>
                  <div className="col-span-2 md:col-span-1">
                    <span className="font-serif text-3xl text-[#3A8FA5] leading-none">{p.n}</span>
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-serif text-2xl mb-2">{p.t}</h3>
                    <p className="text-[#5F758F] leading-relaxed">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <aside className="lg:col-span-5 lg:col-start-8">
              <div className="bg-[#1E3A5F] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#3A8FA5]/25 blur-3xl pointer-events-none" />
                <div className="relative flex items-center gap-3 mb-8">
                  <ShieldCheck size={22} weight="duotone" className="text-[#7FB5C4]" />
                  <p className="text-[11px] tracking-[0.3em] uppercase text-white/70">En bref</p>
                </div>
                <ul className="relative space-y-4">
                  {[
                    "Hébergement certifié HDS, en France",
                    "Traçabilité complète des accès et des actions",
                    "Conforme au RGPD avec audits de sécurité réguliers",
                    "Aucune réutilisation des données à d'autres fins",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-[#3A8FA5] flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={12} weight="bold" className="text-white" />
                      </span>
                      <span className="text-sm md:text-base leading-relaxed">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 py-32 bg-white border-t border-[#E1E9F0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Ils utilisent Questiomed</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Une équipe médicale, pas un éditeur SaaS.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                q: "J'envoie mon PHQ-9 en 20 secondes, le score arrive avant même la consultation. Ça change ma journée.",
                a: "Dr. Camille R.", r: "Psychiatre libérale, Lyon",
              },
              {
                q: "Le suivi longitudinal Oswestry sur mes patients lombalgiques est enfin lisible. Fini le classeur.",
                a: "Dr. Julien M.", r: "Chirurgien du rachis, Marseille",
              },
              {
                q: "L'extraction IA des comptes-rendus m'a fait gagner des semaines sur ma cohorte de recherche.",
                a: "Dr. Sophie L.", r: "PH — Recherche clinique, Paris",
              },
            ].map(({ q, a, r }, i) => (
              <motion.div
                key={a}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#F4F7FB] rounded-3xl p-8 border border-[#E1E9F0] flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Quotes size={28} weight="fill" className="text-[#3A8FA5]/40 mb-4" />
                <p className="text-[#1E3A5F] leading-relaxed mb-6 flex-1">{q}</p>
                <div className="pt-4 border-t border-[#E1E9F0]">
                  <p className="text-sm font-semibold text-[#1E3A5F]">{a}</p>
                  <p className="text-xs text-[#5F758F] mt-0.5">{r}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="px-6 pt-32 pb-32 bg-[#F4F7FB]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span id="pricing" className="block scroll-mt-24" aria-hidden="true" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Tarifs</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">Simple et transparent</h2>
            <p className="text-lg text-[#5F758F]">15 jours d'essai gratuit — aucune carte bancaire requise.</p>
            <div className="mt-6 inline-flex items-center gap-2">
              <Tag size={14} weight="duotone" className="text-[#3A8FA5]" />
              <div className="inline-flex bg-white border border-[#E1E9F0] rounded-full p-0.5">
                <button
                  onClick={() => setYearly(false)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${!yearly ? "bg-[#3A8FA5] text-white" : "text-[#5F758F]"}`}
                  data-testid="landing-toggle-monthly"
                >Mensuel</button>
                <button
                  onClick={() => setYearly(true)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${yearly ? "bg-[#3A8FA5] text-white" : "text-[#5F758F]"}`}
                  data-testid="landing-toggle-yearly"
                >Annuel <span className={`ml-1 ${yearly ? "text-white/90" : "text-[#3A8FA5]"} font-semibold`}>-10%</span></button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-[#E1E9F0] rounded-2xl p-8 bg-white flex flex-col">
              <p className="text-xs tracking-[0.25em] uppercase text-[#3A8FA5] mb-2">Standard</p>
              <p className="text-sm italic text-[#5F758F] mb-4">Pour la pratique quotidienne</p>
              <div className="flex items-baseline gap-2 mb-1">
                <p className="font-serif text-5xl">{stdPrice}€</p>
                <p className="text-sm text-[#5F758F]">{stdSuffix}</p>
              </div>
              {yearly && <p className="text-xs text-[#3A8FA5] font-medium mb-5">Économisez 34,80€ / an</p>}
              {!yearly && <div className="mb-5" />}
              <div className="text-xs text-[#5F758F] mb-4 flex items-center gap-1.5" data-testid="std-seats-badge">
                <UsersThree size={14} weight="duotone" className="text-[#3A8FA5]" />
                <span><strong className="text-[#1E3A5F]">1 praticien</strong> + jusqu'à <strong className="text-[#1E3A5F]">4 assistants</strong> inclus</span>
              </div>
              <p className="text-sm font-semibold text-[#1E3A5F] mb-3">Fonctionnalités incluses dans Standard :</p>
              <ul className="space-y-2.5 text-sm text-[#1E3A5F] mb-8 flex-1">
                {[
                  "Questionnaires illimités",
                  "Bibliothèque validée (EVA, PHQ-9, GAD-7…)",
                  "Envois illimités",
                  "Accès collaborateur et secrétariat",
                  "Conformité RGPD & archivage sur serveur HDS en France",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Sparkle size={13} weight="fill" className="text-[#3A8FA5] shrink-0 mt-1" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" data-testid="pricing-cta-base">
                <Button className="w-full rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white">Commencer l'essai</Button>
              </Link>
              <p className="text-xs text-[#3A8FA5] font-medium text-center mt-3">15 jours d'essai gratuit — sans CB</p>
            </div>

            <div className="border-2 border-[#3A8FA5] rounded-2xl p-8 bg-white flex flex-col relative shadow-lg">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3A8FA5] text-white text-xs tracking-widest uppercase px-3 py-1 rounded-full">Recommandé</span>
              <p className="text-xs tracking-[0.25em] uppercase text-[#3A8FA5] mb-2">Premium</p>
              <p className="text-sm italic text-[#3A8FA5] mb-4">Pour la recherche clinique</p>
              <div className="flex items-baseline gap-2 mb-1">
                <p className="font-serif text-5xl">{premPrice}€</p>
                <p className="text-sm text-[#5F758F]">{premSuffix}</p>
              </div>
              {yearly && <p className="text-xs text-[#3A8FA5] font-medium mb-5">Économisez 58,80€ / an</p>}
              {!yearly && <div className="mb-5" />}
              <div className="text-xs text-[#5F758F] mb-4 flex items-center gap-1.5" data-testid="prem-seats-badge">
                <UsersThree size={14} weight="duotone" className="text-[#3A8FA5]" />
                <span><strong className="text-[#1E3A5F]">1 praticien</strong> + jusqu'à <strong className="text-[#1E3A5F]">4 assistants</strong> inclus</span>
              </div>
              <p className="text-sm font-semibold text-[#1E3A5F] mb-3">Toutes les fonctionnalités de Standard, plus :</p>
              <ul className="space-y-2.5 text-sm text-[#1E3A5F] mb-8 flex-1">
                {[
                  "Statistiques anonymisées",
                  "Comparaison de cohortes",
                  "Analyse des données de vos compte-rendus par IA",
                  "Accès assistant de recherche clinique",
                  "Exports CSV",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Sparkle size={13} weight="fill" className="text-[#3A8FA5] shrink-0 mt-1" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" data-testid="pricing-cta-premium">
                <Button className="w-full rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white">Commencer l'essai</Button>
              </Link>
              <p className="text-xs text-[#3A8FA5] font-medium text-center mt-3">15 jours d'essai gratuit — sans CB</p>
            </div>

            <div
              className={`rounded-2xl p-8 bg-white flex flex-col transition-all duration-500 ${isCustomQuote ? "border-2 border-[#E4572E]/60 shadow-[0_20px_60px_-25px_rgba(228,87,46,0.35)] bg-gradient-to-br from-white via-white to-[#FFF3EE]" : "border border-[#E1E9F0]"}`}
              data-testid="pricing-card-enterprise"
            >
              <p className="text-xs tracking-[0.25em] uppercase text-[#3A8FA5] mb-2 flex items-center gap-2">
                <Buildings size={14} weight="duotone" /> Établissements de santé
              </p>
              <p className="text-sm italic text-[#5F758F] mb-5">Cabinets multi-praticiens, cliniques, MSP</p>

              {/* Slider sièges */}
              <div className="mb-5">
                <div className="flex items-baseline justify-between mb-2.5">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#5F758F]">Sièges praticien</span>
                  <div className="flex items-baseline gap-1 font-mono tabular-nums">
                    <span className={`text-3xl font-serif transition-colors ${isCustomQuote ? "text-[#E4572E]" : "text-[#1E3A5F]"}`} data-testid="landing-seats-count">
                      {isCustomQuote ? `${SEATS_MAX}+` : seats}
                    </span>
                  </div>
                </div>
                <SliderPrimitive.Root
                  value={[seats]}
                  min={SEATS_MIN}
                  max={SEATS_MAX}
                  step={1}
                  onValueChange={([v]) => setSeats(v)}
                  className="relative flex w-full touch-none select-none items-center h-6 group"
                  data-testid="landing-seats-slider"
                  aria-label="Nombre de sièges praticien"
                >
                  <SliderPrimitive.Track className={`relative h-1.5 w-full grow overflow-hidden rounded-full ${isCustomQuote ? "bg-[#E4572E]/15" : "bg-[#E1E9F0]"}`}>
                    <SliderPrimitive.Range className={`absolute h-full transition-colors duration-300 ${isCustomQuote ? "bg-[#E4572E]" : "bg-[#3A8FA5]"}`} />
                  </SliderPrimitive.Track>
                  <SliderPrimitive.Thumb
                    className={`block h-5 w-5 rounded-full border-2 shadow-lg transition-all duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-4 ${isCustomQuote ? "bg-[#E4572E] border-white focus-visible:ring-[#E4572E]/30" : "bg-white border-[#3A8FA5] focus-visible:ring-[#3A8FA5]/30"}`}
                  />
                </SliderPrimitive.Root>
                <div className="flex justify-between mt-2 text-[10px] text-[#8494A8] font-mono tabular-nums pointer-events-none">
                  <span>3</span>
                  <span className="ml-[10%]">5</span>
                  <span className="mr-[27%]">10</span>
                  <span className={isCustomQuote ? "text-[#E4572E] font-semibold" : "text-[#5F758F]"}>15+</span>
                </div>
                {/* ROI — temps secrétariat économisé (~ 4h/semaine/praticien) — cliquable → détail DSI */}
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="mt-3 w-full flex items-center gap-2 rounded-lg bg-[#F4F7FB] border border-[#E1E9F0] px-3 py-2 text-left hover:border-[#3A8FA5] hover:bg-white hover:shadow-sm transition-all group"
                      data-testid="landing-roi-badge"
                      aria-label="Voir le détail du calcul de ROI"
                    >
                      <Clock size={14} weight="duotone" className="text-[#3A8FA5] shrink-0" />
                      <p className="text-xs text-[#5F758F] leading-snug flex-1">
                        ≈ <strong className="text-[#1E3A5F] tabular-nums" data-testid="landing-roi-hours">{isCustomQuote ? `${SEATS_MAX * 4}+` : seats * 4}h</strong> de temps secrétariat économisées / semaine
                        {seats > 1 && !isCustomQuote && <span className="text-[#8494A8]"> · 4h × {seats} praticiens</span>}
                      </p>
                      <Info size={13} weight="bold" className="text-[#3A8FA5] shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg bg-white p-0 overflow-hidden" data-testid="landing-roi-dialog">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E1E9F0]">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#3A8FA5]/10 flex items-center justify-center shrink-0">
                          <Calculator size={20} weight="duotone" className="text-[#3A8FA5]" />
                        </div>
                        <div className="flex-1">
                          <DialogTitle className="text-lg font-serif text-[#1E3A5F] leading-tight">
                            Retour sur investissement — argumentaire DSI
                          </DialogTitle>
                          <p className="text-xs text-[#5F758F] mt-1">Simulation basée sur {roiSeatsForCalc} praticien{roiSeatsForCalc > 1 ? "s" : ""}{isCustomQuote ? " (base sur-mesure ≥ 15)" : ""}</p>
                        </div>
                      </div>
                    </DialogHeader>

                    <div className="px-6 py-5 space-y-4">
                      {/* Formule de base par praticien */}
                      <div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5F758F] mb-2">Calcul par praticien</p>
                        <div className="rounded-xl border border-[#E1E9F0] overflow-hidden text-sm">
                          <div className="flex items-center justify-between px-4 py-2.5 bg-[#F4F7FB]">
                            <span className="text-[#5F758F]">Temps secrétariat économisé</span>
                            <span className="font-mono tabular-nums text-[#1E3A5F]"><strong>4</strong> h / semaine</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E1E9F0]">
                            <span className="text-[#5F758F]">Coût horaire chargé secrétariat médical <span className="text-[10px] text-[#8494A8]">(France, 2025)</span></span>
                            <span className="font-mono tabular-nums text-[#1E3A5F]"><strong>44</strong> € / h</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E1E9F0]">
                            <span className="text-[#5F758F]">Semaines par mois</span>
                            <span className="font-mono tabular-nums text-[#1E3A5F]"><strong>4</strong></span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-[#3A8FA5]/5 border-t-2 border-[#3A8FA5]/20">
                            <span className="text-[#1E3A5F] font-semibold">4 × 44 × 4 =</span>
                            <span className="font-mono tabular-nums text-[#3A8FA5] text-lg font-serif"><strong>{fmt(roiPerPractitionerMonthly, 0)} €</strong> / mois</span>
                          </div>
                        </div>
                      </div>

                      {/* Projection */}
                      <div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5F758F] mb-2">Projection sur {roiSeatsForCalc} praticien{roiSeatsForCalc > 1 ? "s" : ""}</p>
                        <div className="rounded-xl border border-[#E1E9F0] overflow-hidden text-sm">
                          <div className="flex items-center justify-between px-4 py-2.5 bg-[#F4F7FB]">
                            <span className="text-[#5F758F]">Économies mensuelles totales</span>
                            <span className="font-mono tabular-nums text-[#1E3A5F]"><strong>{fmt(roiMonthlyTotal, 0)}</strong> €</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E1E9F0]">
                            <span className="text-[#5F758F]">Économies annuelles</span>
                            <span className="font-mono tabular-nums text-[#1E3A5F]"><strong>{fmt(roiYearlyTotal, 0)}</strong> €</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E1E9F0]">
                            <span className="text-[#5F758F]">Coût annuel Questiomed <span className="text-[10px] text-[#8494A8]">({yearly ? "annuel −10 %" : "mensuel"})</span></span>
                            <span className="font-mono tabular-nums text-[#5F758F]">−{fmt(subscriptionYearly, 0)} €</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-t-2 border-emerald-500/30">
                            <span className="text-emerald-900 font-semibold flex items-center gap-1.5">
                              <TrendUp size={15} weight="bold" /> Économies nettes annuelles
                            </span>
                            <span className="font-mono tabular-nums text-emerald-700 text-lg font-serif" data-testid="landing-roi-net-yearly">
                              <strong>{fmt(roiNetYearly, 0)} €</strong>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* ROI multiple */}
                      <div className="rounded-xl bg-gradient-to-br from-[#3A8FA5]/10 to-[#164E5F]/10 border border-[#3A8FA5]/25 p-4 text-center">
                        <p className="text-[10px] tracking-[0.25em] uppercase text-[#3A8FA5] mb-1">Multiplicateur de ROI</p>
                        <p className="font-serif text-4xl text-[#164E5F] tabular-nums" data-testid="landing-roi-multiple">
                          × {fmt(roiMultiple, 1)}
                        </p>
                        <p className="text-xs text-[#5F758F] mt-1">
                          Chaque euro investi dans Questiomed retourne <strong className="text-[#164E5F]">{fmt(roiMultiple, 1)} €</strong> en temps secrétariat économisé
                        </p>
                      </div>

                      <p className="text-[11px] text-[#8494A8] leading-relaxed">
                        <strong>Sources et hypothèses.</strong> Coût horaire chargé (salaire brut + charges patronales) d'une secrétaire médicale en France : moyenne 44 €/h (INSEE / conventions collectives 2025). Gain de 4 h/semaine mesuré sur retours praticiens Questiomed : envois de questionnaires automatisés, saisie éliminée, comptes-rendus pré-remplis. Chiffres HT à ajuster selon votre convention et votre volumétrie.
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Prix live */}
              <AnimatePresence mode="wait" initial={false}>
                {isCustomQuote ? (
                  <motion.div
                    key="custom"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="mb-5"
                  >
                    <div className="flex items-baseline gap-2 mb-1">
                      <p className="font-serif text-4xl text-[#E4572E]" data-testid="landing-price-custom">Sur mesure</p>
                    </div>
                    <p className="text-xs text-[#E4572E] font-medium">Offre entreprise — accompagnement dédié</p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="live"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="mb-5"
                  >
                    <div className="flex items-baseline gap-2 mb-1 tabular-nums">
                      <p className="font-serif text-5xl" data-testid="landing-price-total">{fmt(seatTotal, yearly ? 2 : 0)}€</p>
                      <p className="text-sm text-[#5F758F]">{yearly ? "/ mois HT, facturé annuellement" : "/ mois HT"}</p>
                    </div>
                    <p className="text-xs text-[#5F758F]">
                      Soit <strong className="text-[#1E3A5F]">{fmt(seatUnit, 2)}€</strong>/siège/mois × {seats} sièges
                    </p>
                    {yearly && (
                      <p className="text-xs text-[#3A8FA5] font-medium mt-1" data-testid="landing-yearly-savings">
                        Économisez {fmt(seatYearlySavings, 2)}€ par an
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="text-xs text-[#5F758F] mb-4 flex items-center gap-1.5">
                <UsersThree size={14} weight="duotone" className="text-[#3A8FA5]" />
                <span><strong className="text-[#1E3A5F]">Assistants illimités</strong> par praticien · accès inter-cabinets</span>
              </div>
              <p className="text-sm font-semibold text-[#1E3A5F] mb-3">Toutes les fonctionnalités de Premium, plus :</p>
              <ul className="space-y-2.5 text-sm text-[#1E3A5F] mb-8 flex-1">
                {[
                  "Gestion multi-praticiens & multi-cabinets",
                  "Facturation centralisée par siège",
                  "Onboarding personnalisé",
                  "Support prioritaire",
                  isCustomQuote ? "Intégration DPI / SSO / API dédiée" : "Intégration DPI / SSO / API sur demande",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Sparkle size={13} weight="fill" className={`shrink-0 mt-1 ${isCustomQuote ? "text-[#E4572E]" : "text-[#3A8FA5]"}`} /><span>{f}</span>
                  </li>
                ))}
              </ul>

              <AnimatePresence mode="wait" initial={false}>
                {isCustomQuote ? (
                  <motion.a
                    key="cta-quote"
                    href={mailtoQuote}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    data-testid="pricing-cta-enterprise-quote"
                  >
                    <Button className="w-full rounded-full bg-[#E4572E] hover:bg-[#F27551] text-white">
                      <Envelope size={16} weight="bold" className="mr-2" /> Demander un devis
                    </Button>
                  </motion.a>
                ) : (
                  <motion.div
                    key="cta-signup"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  >
                    <Link to={`/signup?plan=establishment&seats=${seats}&interval=${yearly ? "yearly" : "monthly"}`} data-testid="pricing-cta-enterprise">
                      <Button className="w-full rounded-full bg-[#1E3A5F] hover:bg-[#0E2540] text-white">
                        Créer notre établissement
                      </Button>
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
              <p className="text-xs text-[#3A8FA5] font-medium text-center mt-3">
                {isCustomQuote ? "Réponse sous 48h ouvrées" : "15 jours d'essai gratuit — sans CB"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 pt-32 pb-32 bg-white border-t border-[#E1E9F0]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span id="faq" className="block scroll-mt-24" aria-hidden="true" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">Questions fréquentes</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Vous avez une question ?</h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "Puis-je créer mes propres questionnaires ?",
                a: "Absolument. Notre outil intégré vous permet de créer des questions de type choix uniques/multiples, texte libre, échelles 0-10, échelles Likert, dates, oui/non. Vous pouvez également configurer le calcul de score automatique." },
              { q: "Mes patients doivent-ils créer un compte ?",
                a: "Non. Chaque questionnaire est envoyé via un lien unique et sécurisé. Le patient répond sans identifiants, sur ordinateur ou mobile." },
              { q: "Puis-je partager mon abonnement avec mon secrétariat ou un collaborateur ?",
                a: "Tout à fait. Dans l'offre Standard, vous pouvez ajouter un utilisateur qui pourra créer/modifier vos patients et leur envoyer des questionnaires. Dans l'offre Premium, vous pourrez ajouter un utilisateur dédié à la recherche clinique qui pourra réaliser des études statistiques et analyser vos comptes-rendus par IA." },
              { q: "Puis-je exporter les réponses des patients ?",
                a: "Oui : chaque réponse peut être exportée en PDF pour votre dossier patient. L'offre Premium permet l'export CSV pour vos analyses statistiques." },
              { q: "Les données de mes patients sont-elles hébergées en France ?",
                a: "Oui. Toutes les données sont stockées sur des serveurs certifiés HDS (Hébergeur de Données de Santé) en France. Aucun transfert hors de l'Union Européenne." },
              { q: "L'application est-elle conforme au RGPD ?",
                a: "Oui — par conception. Vous êtes responsable de traitement, Questiomed est sous-traitant. Un contrat de sous-traitance (DPA) est disponible sur demande." },
              { q: "Que se passe-t-il à la fin de l'essai gratuit ?",
                a: "À la fin des 15 jours d'essai gratuit, vous choisissez de souscrire ou non. Sans action de votre part, votre compte passera en lecture seule." },
              { q: "Puis-je résilier à tout moment ?",
                a: "Oui. Les abonnements mensuels sont résiliables à tout moment. Les abonnements annuels s'arrêtent à la fin de la période souscrite, conformément à l'engagement." },
            ].map(({ q, a }, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={q}
                  className={`rounded-2xl border transition-colors ${isOpen ? "bg-[#F4F7FB] border-[#3A8FA5]/40" : "bg-white border-[#E1E9F0]"}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between text-left px-6 py-5 gap-4"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-serif text-lg text-[#1E3A5F]">{q}</span>
                    <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform ${isOpen ? "bg-[#3A8FA5] text-white rotate-180" : "bg-[#EEF3F8] text-[#3A8FA5]"}`}>
                      <CaretDown size={16} weight="bold" />
                    </span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-sm text-[#5F758F] leading-relaxed">{a}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-6 py-32 bg-[#F4F7FB]">
        <div className="max-w-4xl mx-auto relative">
          <div className="relative rounded-[36px] bg-[#1E3A5F] text-white px-10 md:px-16 py-20 text-center overflow-hidden">
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#3A8FA5]/30 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-[#D45A3F]/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-6">
                Digitalisez votre pratique <span className="italic text-[#3A8FA5]">aujourd'hui</span>.
              </h2>
              <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
                Créez votre compte en 30 secondes et accédez à toutes les fonctionnalités de Questiomed.
              </p>
              <Link to="/signup" data-testid="footer-cta-signup">
                <Button size="lg" className="rounded-full bg-white hover:bg-[#EEF3F8] text-[#1E3A5F] px-10 py-7 text-base group">
                  Commencer maintenant
                  <ArrowRight size={18} weight="bold" className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <p className="text-xs text-white/50 mt-6">15 jours d'essai gratuit · Aucune carte bancaire requise</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E1E9F0] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
            <span className="text-[16px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-[14px]">
            <Link to="/mentions-legales" className="text-[#5F758F] hover:text-[#1E3A5F] font-semibold" data-testid="footer-legal-mentions">Mentions légales</Link>
            <Link to="/cgu" className="text-[#5F758F] hover:text-[#1E3A5F] font-semibold" data-testid="footer-legal-cgu">CGU</Link>
            <Link to="/cgv" className="text-[#5F758F] hover:text-[#1E3A5F] font-semibold" data-testid="footer-legal-cgv">CGV</Link>
            <Link to="/confidentialite" className="text-[#5F758F] hover:text-[#1E3A5F] font-semibold" data-testid="footer-legal-privacy">Politique de confidentialité</Link>
            <a href="mailto:contact@questiomed.fr" className="text-[#5F758F] hover:text-[#1E3A5F] font-semibold">Contact</a>
          </div>
        </div>
        <div className="border-t border-[#E1E9F0] py-4 px-6 text-center text-[12px] text-[#5F758F]">
          © 2026 Questiomed — Digitalisation des questionnaires cliniques. Hébergement HDS France.
        </div>
      </footer>
    </div>
  );
}

/* ----------------- Mockups (pure CSS/JSX, no images) ----------------- */

// Animated builder preview: a card that mimics the app's question builder.
// Kept inside this file to avoid creating a new component for a one-off marketing visual.
function BuilderMockup() {
  return (
    <div className="relative w-full" data-testid="builder-mockup">
      <div className="rounded-2xl border border-[#E1E9F0] bg-white shadow-2xl shadow-[#1E3A5F]/10 overflow-hidden">
        {/* Top action bar */}
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-[#E1E9F0] bg-[#F4F7FB]">
          <span className="text-[11px] tracking-[0.15em] uppercase text-[#5F758F]">Nouveau questionnaire</span>
          <div className="flex items-center gap-1.5">
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-[#5F758F] border border-[#E1E9F0] bg-white">
              <Sparkle size={11} weight="fill" className="text-[#3A8FA5]" /> Régénérer avec l'IA
            </span>
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-[#5F758F] border border-[#E1E9F0] bg-white">
              <Eye size={11} weight="regular" /> Prévisualiser
            </span>
            <span className="text-[11px] px-2.5 py-1 rounded-md text-white bg-[#3A8FA5] font-medium">Enregistrer</span>
          </div>
        </div>

        {/* Header meta */}
        <div className="px-5 py-4 border-b border-[#E1E9F0]">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5F758F] mb-1">Titre du questionnaire</p>
          <p className="text-[15px] font-semibold text-[#1E3A5F]">PHQ-9 — Dépression</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF3F8] text-[#3A8FA5] tracking-wide">Psychiatrie</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4F7FB] text-[#5F758F] tracking-wide border border-[#E1E9F0]">9 items</span>
          </div>
        </div>

        {/* Question cards */}
        <div className="p-4 space-y-2">
          {[
            { n: 1, q: "Peu d'intérêt ou de plaisir à faire les choses", t: "Échelle 0-3" },
            { n: 2, q: "Se sentir triste, déprimé(e), désespéré(e)", t: "Échelle 0-3" },
            { n: 3, q: "Difficultés d'endormissement ou sommeil excessif", t: "Échelle 0-3" },
          ].map((r) => (
            <div key={r.n} className="rounded-xl border border-[#E1E9F0] bg-[#FAFBFD] px-3 py-2.5 flex items-center gap-3">
              <span className="text-[#B7C4D6] leading-none select-none text-[15px]">⠿</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[#EEF3F8] text-[#3A8FA5] tracking-wider font-medium">Q{r.n}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[#1E3A5F] truncate">{r.q}</p>
                <p className="text-[10px] text-[#5F758F] mt-0.5">{r.t} · Obligatoire · Poids ×1</p>
              </div>
            </div>
          ))}
          <div className="rounded-xl border-2 border-dashed border-[#3A8FA5]/40 py-2 flex items-center justify-center text-[12px] font-medium text-[#3A8FA5]">
            <Plus size={12} weight="bold" className="mr-1" /> Ajouter une question
          </div>
        </div>

        {/* Scoring footer */}
        <div className="border-t border-[#E1E9F0] bg-[#F4F7FB] px-5 py-3 flex items-center justify-between text-[11px] text-[#5F758F]">
          <span className="tracking-[0.15em] uppercase">Scoring · Somme totale</span>
          <div className="flex gap-1">
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#3A8FA5]/10 text-[#3A8FA5] font-medium">0-9</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#F59E0B]/15 text-[#B8770B] font-medium">10-19</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#D45A3F]/15 text-[#B8452F] font-medium">20-27</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 02 — Send: modeled after the real SendDialog (Étape 1 + Étape 2)
function SendMockup() {
  return (
    <div className="relative w-full" data-testid="send-mockup">
      <div className="rounded-2xl border border-[#E1E9F0] bg-white shadow-2xl shadow-[#1E3A5F]/10 overflow-hidden">
        {/* Modal title */}
        <div className="px-6 py-5 border-b border-[#E1E9F0]">
          <p className="font-serif text-xl text-[#1E3A5F]">Envoyer un questionnaire</p>
          <p className="text-[12px] text-[#5F758F] mt-0.5">PHQ-9 — Dépression</p>
        </div>

        {/* Étape 1 · Patient */}
        <div className="px-6 py-4 border-b border-[#E1E9F0]/70">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#3A8FA5] mb-3">Étape 1 · Patient</p>
          <div className="flex items-center gap-3 border border-[#E1E9F0] rounded-xl bg-[#FAFBFD] px-3 py-2.5">
            <span className="w-8 h-8 rounded-full bg-[#EEF3F8] flex items-center justify-center text-[12px] font-semibold text-[#3A8FA5]">AL</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-[#1E3A5F]">Alexandre Leclerc</p>
              <p className="text-[11px] text-[#5F758F]">15 mai 1982 · a.leclerc@courrier.fr</p>
            </div>
          </div>
        </div>

        {/* Étape 2 · Méthode */}
        <div className="px-6 py-4">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#3A8FA5] mb-3">Étape 2 · Comment l'envoyer ?</p>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-xl border-2 border-[#3A8FA5] bg-[#EEF3F8]/60 px-3 py-3 relative">
              <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-[#3A8FA5] flex items-center justify-center">
                <Check size={9} weight="bold" className="text-white" />
              </span>
              <PaperPlaneTilt size={18} weight="duotone" className="text-[#3A8FA5] mb-2" />
              <p className="text-[13px] font-semibold text-[#1E3A5F]">Email au patient</p>
              <p className="text-[10px] text-[#5F758F] mt-0.5">Envoi immédiat</p>
            </div>
            <div className="rounded-xl border border-[#E1E9F0] bg-white px-3 py-3">
              <FileText size={18} weight="regular" className="text-[#5F758F] mb-2" />
              <p className="text-[13px] font-semibold text-[#1E3A5F]/70">Lien à partager</p>
              <p className="text-[10px] text-[#5F758F] mt-0.5">SMS, WhatsApp, imprimé…</p>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-[#5F758F] bg-[#F4F7FB] rounded-lg px-3 py-2 border border-[#E1E9F0]">
            <Lock size={12} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
            <span>L'email ne contient ni le nom du patient, ni celui du questionnaire.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#F4F7FB] border-t border-[#E1E9F0]">
          <span className="text-[12px] text-[#5F758F]">Annuler</span>
          <div className="flex items-center gap-2 bg-[#3A8FA5] text-white text-[13px] font-medium px-4 py-2 rounded-full">
            <PaperPlaneTilt size={13} weight="fill" />
            Envoyer l'email
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 03 — Results: modeled after the real ResponseDetail screen
function ResultsMockup() {
  const items = [
    { q: "1. Peu d'intérêt ou de plaisir à faire les choses", a: "Presque tous les jours" },
    { q: "2. Se sentir triste, déprimé(e), désespéré(e)", a: "Plus de la moitié du temps" },
    { q: "3. Difficultés d'endormissement ou sommeil excessif", a: "Plusieurs jours" },
  ];
  return (
    <div className="relative w-full" data-testid="results-mockup">
      <div className="rounded-2xl border border-[#E1E9F0] bg-white shadow-2xl shadow-[#1E3A5F]/10 overflow-hidden">
        {/* Top actions */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E1E9F0] bg-[#F4F7FB]">
          <span className="text-[12px] text-[#5F758F]">← Retour</span>
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#3A8FA5] px-2.5 py-1 rounded-md border border-[#3A8FA5]/40">
            <FileText size={12} weight="duotone" /> Télécharger PDF
          </span>
        </div>

        {/* Head card */}
        <div className="p-5 border-b border-[#E1E9F0] flex items-start gap-4">
          <span className="w-11 h-11 rounded-xl bg-[#EEF3F8] flex items-center justify-center text-[15px] font-semibold text-[#3A8FA5] shrink-0">AL</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1E3A5F] text-[15px]">Alexandre Leclerc</p>
            <p className="text-[11px] text-[#5F758F] mt-0.5">PHQ-9 — Dépression</p>
            <p className="text-[11px] text-[#5F758F]">Reçu le 12 févr. 2026 · 14h32</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#5F758F]">Score global</p>
            <p className="font-serif text-3xl leading-none text-[#1E3A5F] mt-1">14 <span className="text-sm text-[#5F758F]">/ 27</span></p>
            <span className="mt-1.5 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#B8770B]">Dépression modérée</span>
          </div>
        </div>

        {/* Réponses détaillées */}
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#EEF3F8] text-[#3A8FA5] font-semibold tracking-wide">9</span>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#5F758F]">Réponses détaillées</p>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border border-[#E1E9F0] rounded-lg px-3 py-2">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-semibold text-[#3A8FA5] mt-0.5">Q{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-[#1E3A5F] leading-snug">{it.q}</p>
                    <p className="text-[12px] font-medium text-[#3A8FA5] mt-1">{it.a}</p>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-[11px] italic text-[#5F758F] pt-1 pl-1">+ 6 autres items…</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Patient-side response mockup shown next to the "How it works" section
function PatientResponseMockup() {
  return (
    <div className="relative" data-testid="patient-mockup">
      <div className="rounded-3xl border border-[#E1E9F0] bg-white shadow-2xl shadow-[#1E3A5F]/10 overflow-hidden">
        <div className="bg-[#EEF3F8] px-6 py-5 border-b border-[#E1E9F0]">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#3A8FA5]">Envoyé par Dr. Camille R.</p>
          <p className="font-serif text-2xl text-[#1E3A5F] mt-1">Questionnaire Oswestry</p>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-sm font-semibold text-[#1E3A5F] mb-3">1. Intensité de la douleur</p>
            <div className="grid grid-cols-6 gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`h-9 rounded-lg text-sm font-semibold transition-colors ${n === 2 ? "bg-[#3A8FA5] text-white" : "bg-[#F4F7FB] text-[#5F758F] border border-[#E1E9F0]"}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-[#5F758F] uppercase tracking-wider">
              <span>Aucune</span><span>Insupportable</span>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1E3A5F] mb-3">2. Marche</p>
            <div className="space-y-2">
              {[
                "Je peux marcher sans douleur",
                "Je peux marcher, mais avec un peu de douleur",
              ].map((t, i) => (
                <label key={t} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${i === 1 ? "bg-[#EEF3F8] border-[#3A8FA5]/40" : "bg-white border-[#E1E9F0]"}`}>
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${i === 1 ? "border-[#3A8FA5]" : "border-[#B7C4D6]"}`}>
                    {i === 1 && <span className="w-2 h-2 rounded-full bg-[#3A8FA5]" />}
                  </span>
                  <span className="text-[13px] text-[#1E3A5F]">{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="pt-3">
            <div className="rounded-xl bg-[#3A8FA5] text-white text-center py-3 text-sm font-semibold">
              Valider ma réponse
            </div>
          </div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="absolute -top-4 -right-4 bg-white border border-[#E1E9F0] rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3"
      >
        <div className="w-9 h-9 rounded-full bg-[#3A8FA5]/10 flex items-center justify-center">
          <Lock size={18} weight="duotone" className="text-[#3A8FA5]" />
        </div>
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-[#5F758F]">Lien unique</p>
          <p className="text-sm font-semibold text-[#1E3A5F]">Sans compte patient</p>
        </div>
      </motion.div>
    </div>
  );
}
